import React, { useCallback, useEffect, useMemo, useRef, FunctionComponent } from 'react';
// Not using the hook because we need a static reference to avoid re-rendering
// read more: https://github.com/vercel/next.js/discussions/18522#discussioncomment-2417009
import Router from 'next/router';
import { mainLayoutID } from '@modules/layout/layout/constants/layoutConstants';
import ShowInViewMetadata from '../interfaces/ShowInViewMetadata';
import ShowInViewMetadataContext from '../ShowInViewMetadataContext';

const ShowInViewMetadataProvider: FunctionComponent<React.PropsWithChildren> = ({ children }) => {
  /**
   * A `boolean` ref indicating whether we need to reset scroll position (to
   * offset potential content shift incurred by lazy loading) after a hash change
   *
   * `true` - a recent hash change happened and the scroll position need to be
   * reset back to the new hash anchor
   * `false` - otherwise
   */
  const resetScrollPositionRef = useRef<boolean>(false);
  /**
   * A boolean | null ref indicating whether we need to ignore the next scroll
   * event as it is supposed to be incurred by the next `scrollIntoView` done as
   * a after lazy loading callback
   *
   * `null` - no ongoing hash change needs to be handled, see `resetScrollPositionRef`
   * `true` - should skip the next scroll event when handling an ongoing hash change
   * `false` - process the next scroll event when handling an ongoing hash
   * change, i.e. it should cancel all further `scrollIntoView` since the user
   * has scrolled manually
   */
  const skipScrollRef = useRef<boolean | null>(null);

  /**
   * A counter for ongoing scheduled lazy loading tasks, used to determine if
   * all lazy loading triggered by an action is over
   */
  const ongoingTasksCounterRef = useRef<number>(0);

  const handleHashChange = useCallback(() => {
    resetScrollPositionRef.current = window.location.hash !== '';
    skipScrollRef.current = resetScrollPositionRef.current === false ? null : true;
  }, []);

  const handleScroll = useCallback((event: Event) => {
    // only care about the scroll event happening inside the main content
    // container, but not within any nav
    if ((event.target as HTMLElement).id === mainLayoutID) {
      if (skipScrollRef.current === true) {
        // skipped the scroll event
        skipScrollRef.current = false;
      } else if (skipScrollRef.current === false) {
        // process the scroll event as the user manually scrolled
        resetScrollPositionRef.current = false;
        skipScrollRef.current = null;
      }
    }
  }, []);

  // check if the page already have a hash anchor on load already
  useEffect(() => {
    if (typeof window !== 'undefined') {
      handleHashChange();
    }
  }, [handleHashChange]);

  // attach event listeners for hash change - Next.js router handles the hash
  // change if it is done via Next.js APIs
  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);
    Router.events.on('hashChangeComplete', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      Router.events.off('hashChangeComplete', handleHashChange);
    };
  }, [handleHashChange]);

  // attach event listeners for scroll event
  useEffect(() => {
    // set it to use capture to capture event happen inside main DOM
    // set it to passive to improve performance
    // see details: https://github.com/microsoft/TypeScript/issues/32912#issuecomment-522142969
    const options: AddEventListenerOptions & EventListenerOptions = {
      capture: true,
      passive: true,
    };

    document.addEventListener('scroll', handleScroll, options);

    return () => {
      document.removeEventListener('scroll', handleScroll, options);
    };
  });

  const showInViewContextMetadataValue = useMemo<ShowInViewMetadata>(
    () => ({
      resetScrollPositionRef,
      skipScrollRef,
      ongoingTasksCounterRef,
    }),
    [],
  );

  return (
    <ShowInViewMetadataContext.Provider value={showInViewContextMetadataValue}>
      {children}
    </ShowInViewMetadataContext.Provider>
  );
};

export default ShowInViewMetadataProvider;
